import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Mic, Youtube, Flame, Users, Sparkles, Radio } from "lucide-react";
import { MaskedLines, FadeUp } from "@/components/motion/Reveal";
import { SectionLabel } from "@/components/shared";
import Testimonials from "@/components/Testimonials";
import { ASSETS, SITE } from "@/lib/assets";
import { getMinistries } from "@/lib/api";

const MINISTRY_ICONS = { flame: Flame, users: Users, sparkles: Sparkles, radio: Radio };

const EASE = [0.22, 1, 0.36, 1];

const CHAPTERS = [
  { n: "01", title: "Awaken the Church", body: "We call a slumbering Church back to holiness, hunger, and the fire of the Holy Spirit. Comfort is not the goal — Christ is." },
  { n: "02", title: "Disciple Believers", body: "We don't settle for converts. We form disciples who follow Jesus, know the Word, and reproduce their faith in others." },
  { n: "03", title: "Share the Gospel", body: "From small towns to campuses, we take the name of Jesus into the streets of Canada — boldly, clearly, unashamed." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  const [ministries, setMinistries] = useState([]);

  useEffect(() => {
    getMinistries().then(setMinistries).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* ================= HERO ================= */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden">
        <motion.div style={{ y: imgY }} className="absolute inset-0 z-0 scale-110">
          <img src={ASSETS.preachingPodium} alt="Kerby Stivene preaching" className="w-full h-full object-cover" />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-10 bg-black" />
        <div className="absolute inset-0 z-10 spotlight" />
        <div className="absolute inset-0 z-10 bg-gradient-to-t from-black via-transparent to-black/60" />

        <motion.div style={{ y: textY }} className="relative z-20 h-full mx-auto max-w-[1400px] px-5 sm:px-8 flex flex-col justify-end pb-16 md:pb-24">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <SectionLabel>Evangelistic Ministry · Canada</SectionLabel>
          </motion.div>

          <h1 className="mt-6 font-display uppercase text-[15vw] sm:text-[12vw] lg:text-[9.5rem] leading-[0.82] tracking-tight">
            <MaskedLines
              lines={["Awaken", <>The <span className="text-wutn">Nation</span></>]}
              delay={0.35}
              stagger={0.14}
              lineClassName=""
            />
          </h1>

          <div className="mt-8 flex flex-col md:flex-row md:items-end justify-between gap-8">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
              className="text-zinc-300 text-lg max-w-md font-cond uppercase tracking-wide"
            >
              {SITE.tagline}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.8, ease: EASE }}
              className="flex flex-wrap gap-3"
            >
              <Link to="/book" data-testid="hero-book-btn" className="inline-flex items-center gap-2 bg-wutn hover:bg-wutn-dark text-white px-7 py-4 font-cond uppercase tracking-wider text-sm transition-colors">
                <Mic className="h-4 w-4" /> Book Me To Speak
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ================= MARQUEE ================= */}
      <div className="relative z-10 border-y border-white/10 bg-black py-6 overflow-hidden">
        <Marquee speed={45} gradient={false} className="marquee-mask">
          {["Awaken the Church", "Disciple Believers", "Share the Gospel", "Revival in Canada", "No Other Name"].map((t, i) => (
            <span key={i} className="mx-8 inline-flex items-center gap-8 font-display uppercase text-4xl md:text-6xl text-outline">
              {t}
              <span className="text-wutn text-3xl">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* ================= MANIFESTO ================= */}
      <section data-testid="manifesto-section" className="relative z-10 bg-black py-24 md:py-36">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-5">
              <SectionLabel>The Mission</SectionLabel>
              <h2 className="mt-5 font-display uppercase text-5xl md:text-7xl leading-[0.9]">
                Three <span className="text-wutn">Commands</span>, One Fire
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-zinc-400 text-lg leading-relaxed">
                Wake-Up The Nation exists for a single, burning purpose: to see Canada return to Jesus. Everything we do flows from a clear, unshakeable mandate.
              </p>
            </div>
          </div>

          <div className="border-t border-white/10">
            {CHAPTERS.map((c, i) => (
              <FadeUp key={c.n} delay={i * 0.05}>
                <div className="group grid md:grid-cols-12 gap-6 items-start py-10 md:py-14 border-b border-white/10 hover:bg-white/[0.02] transition-colors px-2">
                  <div className="md:col-span-2">
                    <span className="font-display text-6xl md:text-7xl text-outline-red group-hover:text-wutn transition-colors">{c.n}</span>
                  </div>
                  <h3 className="md:col-span-4 font-display uppercase text-3xl md:text-4xl leading-none">{c.title}</h3>
                  <p className="md:col-span-6 text-zinc-400 text-lg leading-relaxed">{c.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ================= MINISTRIES (full) ================= */}
      <section data-testid="ministries-section" className="relative z-10 bg-black py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <div className="lg:col-span-7">
              <SectionLabel>What We Do</SectionLabel>
              <h2 className="mt-5 font-display uppercase text-5xl md:text-7xl leading-[0.9]">Ministries With <span className="text-wutn">Teeth</span></h2>
            </div>
            <p className="lg:col-span-4 lg:col-start-9 text-zinc-400">
              Every arm of Wake-Up The Nation exists to awaken, disciple, and send. Find your place in the mission.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {ministries.map((m, i) => {
              const Icon = MINISTRY_ICONS[m.icon] || Flame;
              return (
                <FadeUp key={m.id} delay={(i % 4) * 0.06}>
                  <div data-testid={`ministry-${m.id}`} className="group bg-black hover:bg-[#0A0A0A] transition-colors p-8 md:p-10 h-full flex flex-col">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex h-14 w-14 items-center justify-center bg-wutn/10 text-wutn group-hover:bg-wutn group-hover:text-white transition-colors">
                        <Icon className="h-7 w-7" />
                      </span>
                      <span className="font-display text-4xl text-outline-red">0{i + 1}</span>
                    </div>
                    <h3 className="mt-6 font-display uppercase text-3xl leading-none">{m.name}</h3>
                    <p className="mt-2 font-cond uppercase tracking-wider text-wutn text-sm">{m.tagline}</p>
                    <p className="mt-4 text-zinc-400 leading-relaxed flex-1">{m.description}</p>
                    <Link to="/book" className="mt-8 inline-flex items-center gap-2 text-white group-hover:text-wutn font-cond uppercase tracking-wider text-sm transition-colors">
                      Get Connected <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= TESTIMONIALS ================= */}
      <Testimonials label="Voices" heading={<>What Pastors<br /><span className="text-wutn">Say</span></>} limit={6} />

      {/* ================= FOUNDER / CTA ================= */}
      <section data-testid="founder-cta" className="relative z-10 bg-[#0A0A0A] border-t border-white/10">
        <div className="grid lg:grid-cols-2">
          <div className="relative min-h-[420px] lg:min-h-[640px] overflow-hidden">
            <img src={ASSETS.founderPortrait} alt="Kerby Stivene" className="absolute inset-0 w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0A0A0A]/60 lg:to-[#0A0A0A]" />
          </div>
          <div className="flex flex-col justify-center p-8 sm:p-14 lg:p-20">
            <SectionLabel>The Voice</SectionLabel>
            <h2 className="mt-6 font-display uppercase text-5xl md:text-6xl leading-[0.9]">
              Led By <span className="text-wutn">Kerby Stivene</span>
            </h2>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed max-w-lg">
A pastor and evangelist with a burden for a sleeping nation. Kerby preaches an uncompromising Gospel and equips ordinary believers to carry it everywhere they go.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/book" data-testid="founder-book-btn" className="inline-flex items-center gap-2 bg-wutn hover:bg-wutn-dark text-white px-7 py-4 font-cond uppercase tracking-wider text-sm transition-colors">
                Book Me To Speak <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/about" data-testid="founder-about-btn" className="inline-flex items-center gap-2 border border-white/30 hover:border-wutn hover:text-wutn px-7 py-4 font-cond uppercase tracking-wider text-sm transition-colors">
                His Story
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
